import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Mail, Lock, PauseCircle, Trash2, ShieldCheck, Loader2, Check, X, Hash, Copy, Globe, Bell, BellOff,
} from 'lucide-react';
import {
  changeEmail, changePassword, deleteAccount, deleteAccountWithOtp,
  pauseAccount, requestDeleteOtp, resendOtp, verifyEmailOtp,
} from '../api/account';
import BlockedUsersSection from '../components/BlockedUsersSection';
import LanguageSwitcher from '../components/LanguageSwitcher';
import BackButton from '../components/BackButton';
import { Switch } from '../components/ui/switch';
import api from '../api/client';
import {
  isPushSupported, getPushSubscription, subscribeToPush, unsubscribeFromPush, sendTestPush,
} from '../lib/pushNotifications';

const Card = ({ icon: Icon, title, subtitle, danger = false, children }) => (
  <div className={`rounded-xl border bg-card p-5 ${danger ? 'border-red-500/40' : 'border-border'}`}>
    <div className="mb-3 flex items-center gap-2">
      <Icon className={`h-4 w-4 ${danger ? 'text-red-400' : 'gold-text'}`} />
      <h3 className={`text-sm font-semibold uppercase tracking-wide ${danger ? 'text-red-400' : 'gold-text'}`}>{title}</h3>
    </div>
    {subtitle && <p className="mb-3 text-xs text-muted-foreground">{subtitle}</p>}
    {children}
  </div>
);

const SettingsPage = () => {
  const { user, refresh, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation('settings');

  const [otp, setOtp] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);

  // Phase 88 — transactional email preferences (optimistic toggle).
  const [emailPrefs, setEmailPrefs] = useState({
    likes: user?.email_notifications_likes !== false,
    messages: user?.email_notifications_messages !== false,
  });
  const toggleEmailPref = async (kind, value) => {
    setEmailPrefs((p) => ({ ...p, [kind]: value }));
    try {
      await api.patch('/auth/me', {
        [kind === 'likes' ? 'email_notifications_likes' : 'email_notifications_messages']: value,
      });
      refresh?.();
      toast({ title: t('email_notifs.saved') });
    } catch (_e) {
      setEmailPrefs((p) => ({ ...p, [kind]: !value }));
      toast({ title: t('email_notifs.error'), variant: 'destructive' });
    }
  };
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwdBusy, setPwdBusy] = useState(false);
  const [emailForm, setEmailForm] = useState({ new_email: '', current_password: '' });
  const [emailBusy, setEmailBusy] = useState(false);
  const [deleteForm, setDeleteForm] = useState({ current_password: '', otp: '', confirm: false });
  const [pauseBusy, setPauseBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteOtpSent, setDeleteOtpSent] = useState(false);
  const [deleteOtpBusy, setDeleteOtpBusy] = useState(false);
  // Social-only accounts (Google/Apple/…) have no password → OTP flow.
  const socialOnly = user?.has_password === false;
  // NeoUI — Web Push
  const pushSupported = isPushSupported();
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  React.useEffect(() => {
    if (!pushSupported) return;
    getPushSubscription().then((s) => setPushEnabled(!!s)).catch(() => {});
  }, [pushSupported]);

  const handlePushToggle = async () => {
    setPushBusy(true);
    try {
      if (pushEnabled) {
        await unsubscribeFromPush();
        setPushEnabled(false);
        toast({ title: t('push.disabled_toast') });
      } else {
        await subscribeToPush();
        setPushEnabled(true);
        toast({ title: t('push.enabled_toast') });
      }
    } catch (e) {
      const msg = e?.message === 'permission_denied' ? t('push.denied') : t('push.error');
      toast({ title: msg, variant: 'destructive' });
    } finally { setPushBusy(false); }
  };

  const handlePushTest = async () => {
    setPushBusy(true);
    try {
      await sendTestPush();
      toast({ title: t('push.test_sent') });
    } catch (_e) {
      toast({ title: t('push.test_error'), variant: 'destructive' });
    } finally { setPushBusy(false); }
  };

  if (!user) return null;

  const handleVerify = async () => {
    if (otp.length !== 6) return toast({ title: t('email_verify.enter_code'), variant: 'destructive' });
    setOtpBusy(true);
    try {
      await verifyEmailOtp(otp);
      await refresh();
      toast({ title: t('email_verify.verify_success') });
      setOtp('');
    } catch (e) {
      toast({ title: t('email_verify.verify_error'), description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setOtpBusy(false); }
  };

  const handleResend = async () => {
    try { await resendOtp(); toast({ title: t('email_verify.resend_success') }); }
    catch (_e) { toast({ title: t('email_verify.resend_error'), variant: 'destructive' }); }
  };

  const handlePwdSubmit = async () => {
    if (pwd.next.length < 6) return toast({ title: t('change_password.min_chars'), variant: 'destructive' });
    if (pwd.next !== pwd.confirm) return toast({ title: t('change_password.mismatch'), variant: 'destructive' });
    setPwdBusy(true);
    try {
      await changePassword(pwd.current, pwd.next);
      toast({ title: t('change_password.success') });
      setPwd({ current: '', next: '', confirm: '' });
    } catch (e) {
      toast({ title: t('change_password.error_title'), description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setPwdBusy(false); }
  };

  const handleEmailSubmit = async () => {
    setEmailBusy(true);
    try {
      await changeEmail(emailForm.new_email, emailForm.current_password);
      await refresh();
      toast({ title: t('change_email.success_title'), description: t('change_email.success_desc') });
      setEmailForm({ new_email: '', current_password: '' });
    } catch (e) {
      toast({ title: t('change_email.error_title'), description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setEmailBusy(false); }
  };

  const handlePause = async () => {
    if (!window.confirm(t('pause.confirm'))) return;
    setPauseBusy(true);
    try {
      await pauseAccount();
      await refresh();
      toast({ title: t('pause.success') });
      navigate('/paused', { replace: true });
    } catch (_e) {
      toast({ title: t('pause.error'), variant: 'destructive' });
    } finally { setPauseBusy(false); }
  };

  const handleRequestDeleteOtp = async () => {
    if (!deleteForm.confirm) return toast({ title: t('delete.tick_required'), variant: 'destructive' });
    setDeleteOtpBusy(true);
    try {
      await requestDeleteOtp();
      setDeleteOtpSent(true);
      toast({ title: t('delete.otp_sent_title'), description: t('delete.otp_sent_desc', { email: user?.email }) });
    } catch (e) {
      toast({ title: t('delete.error_title'), description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setDeleteOtpBusy(false); }
  };

  const handleDelete = async () => {
    if (!deleteForm.confirm) return toast({ title: t('delete.tick_required'), variant: 'destructive' });
    if (socialOnly) {
      if (!deleteForm.otp.trim()) return toast({ title: t('delete.otp_required'), variant: 'destructive' });
    } else if (!deleteForm.current_password) {
      return toast({ title: t('delete.pwd_required'), variant: 'destructive' });
    }
    setDeleteBusy(true);
    try {
      if (socialOnly) await deleteAccountWithOtp(deleteForm.otp.trim());
      else await deleteAccount(deleteForm.current_password);
      toast({ title: t('delete.success_title'), description: t('delete.success_desc') });
      logout();
      navigate('/login', { replace: true });
    } catch (e) {
      toast({ title: t('delete.error_title'), description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setDeleteBusy(false); }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-6" data-testid="settings-page">
      <BackButton className="mb-1" />
      <h1 className="text-2xl font-semibold">{t('page_title')}</h1>

      {/* Phase 39 · Tier 3 — Language section */}
      <Card icon={Globe} title={t('language_section.title')} subtitle={t('language_section.subtitle')}>
        <LanguageSwitcher variant="full" align="start" testId="settings-language-switcher" />
      </Card>

      {/* NeoUI — Browser push notifications */}
      <Card icon={Bell} title={t('push.title')} subtitle={t('push.subtitle')}>
        {!pushSupported ? (
          <p className="text-xs text-muted-foreground" data-testid="push-not-supported">{t('push.not_supported')}</p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={handlePushToggle}
              disabled={pushBusy}
              className={pushEnabled ? 'h-9 bg-secondary text-foreground hover:bg-muted' : 'h-9 gold-gradient'}
              data-testid="push-toggle-btn"
            >
              {pushBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : pushEnabled ? <BellOff className="mr-1.5 h-4 w-4" /> : <Bell className="mr-1.5 h-4 w-4" />}
              {pushEnabled ? t('push.disable') : t('push.enable')}
            </Button>
            {pushEnabled && (
              <Button variant="outline" onClick={handlePushTest} disabled={pushBusy} className="h-9" data-testid="push-test-btn">
                {t('push.test')}
              </Button>
            )}
            <span className={`text-xs ${pushEnabled ? 'text-emerald-400' : 'text-muted-foreground'}`} data-testid="push-status">
              {pushEnabled ? t('push.status_on') : t('push.status_off')}
            </span>
          </div>
        )}
        <p className="mt-2 text-[11px] text-muted-foreground">{t('push.events_hint')}</p>
      </Card>

      {/* Phase 88 — transactional email preferences */}
      <Card icon={Mail} title={t('email_notifs.title')} subtitle={t('email_notifs.subtitle')}>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">{t('email_notifs.likes_label')}</p>
              <p className="text-[11px] text-muted-foreground">{t('email_notifs.likes_desc')}</p>
            </div>
            <Switch
              checked={emailPrefs.likes}
              onCheckedChange={(v) => toggleEmailPref('likes', v)}
              data-testid="email-notif-likes-toggle"
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">{t('email_notifs.messages_label')}</p>
              <p className="text-[11px] text-muted-foreground">{t('email_notifs.messages_desc')}</p>
            </div>
            <Switch
              checked={emailPrefs.messages}
              onCheckedChange={(v) => toggleEmailPref('messages', v)}
              data-testid="email-notif-messages-toggle"
            />
          </div>
        </div>
      </Card>

      {/* Phase 33 — User ID (numeric, permanent, copyable) */}
      <Card icon={Hash} title={t('user_id.title')} subtitle={t('user_id.subtitle')}>
        <div className="flex items-center gap-2">
          <code
            className="rounded-md border border-border bg-background px-3 py-2 text-base font-semibold tracking-wider"
            data-testid="settings-user-number"
          >
            {user.user_number ?? '—'}
          </code>
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(String(user.user_number ?? ''));
                toast({ title: t('user_id.copied_title'), description: t('user_id.copied_desc', { id: user.user_number }) });
              } catch (_) {
                toast({ title: t('user_id.copy_error'), variant: 'destructive' });
              }
            }}
            disabled={!user.user_number}
            data-testid="settings-copy-user-number"
          >
            <Copy className="mr-1.5 h-3.5 w-3.5" /> {t('user_id.copy')}
          </Button>
        </div>
      </Card>

      {/* Email verification */}
      <Card icon={ShieldCheck} title={t('email_verify.title')}
        subtitle={user.email_verified
          ? t('email_verify.verified', { email: user.email })
          : t('email_verify.pending', { email: user.pending_email || user.email })}>
        {!user.email_verified && (
          <div className="flex gap-2">
            <Input
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder={t('email_verify.otp_placeholder')}
              className="h-9 max-w-[140px] tracking-[0.4em] text-center text-sm bg-background"
              data-testid="otp-input"
            />
            <Button onClick={handleVerify} disabled={otpBusy} className="h-9 gold-gradient" data-testid="otp-verify-btn">
              {otpBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />}
              {t('email_verify.verify_btn')}
            </Button>
            <Button variant="ghost" onClick={handleResend} className="h-9 text-xs" data-testid="otp-resend-btn">
              {t('email_verify.resend_btn')}
            </Button>
          </div>
        )}
      </Card>

      {/* Change Email */}
      <Card icon={Mail} title={t('change_email.title')} subtitle={t('change_email.subtitle')}>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <Input
            type="email" placeholder={t('change_email.new_placeholder')}
            value={emailForm.new_email}
            onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
            className="h-9 bg-background"
            data-testid="email-new-input"
          />
          <Input
            type="password" placeholder={t('change_email.current_pwd_placeholder')}
            value={emailForm.current_password}
            onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
            className="h-9 bg-background"
            data-testid="email-pwd-input"
          />
        </div>
        <Button onClick={handleEmailSubmit} disabled={emailBusy} className="mt-2 h-9 gold-gradient" data-testid="email-submit-btn">
          {emailBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('change_email.submit')}
        </Button>
      </Card>

      {/* Change Password */}
      <Card icon={Lock} title={t('change_password.title')} subtitle={t('change_password.subtitle')}>
        <div className="space-y-2">
          <Input type="password" placeholder={t('change_password.current_placeholder')}
            value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
            className="h-9 bg-background" data-testid="pwd-current-input" />
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <Input type="password" placeholder={t('change_password.new_placeholder')}
              value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
              className="h-9 bg-background" data-testid="pwd-new-input" />
            <Input type="password" placeholder={t('change_password.confirm_placeholder')}
              value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
              className="h-9 bg-background" data-testid="pwd-confirm-input" />
          </div>
          <Button onClick={handlePwdSubmit} disabled={pwdBusy} className="h-9 gold-gradient" data-testid="pwd-submit-btn">
            {pwdBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('change_password.submit')}
          </Button>
        </div>
      </Card>

      {/* Pause account */}
      <Card icon={PauseCircle} title={t('pause.title')} subtitle={t('pause.subtitle')}>
        <Button onClick={handlePause} disabled={pauseBusy} variant="outline" className="h-9 border-amber-500/40" data-testid="pause-account-btn">
          {pauseBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('pause.btn')}
        </Button>
      </Card>

      {/* Delete account — danger */}
      <Card icon={Trash2} title={t('delete.title')} danger subtitle={t('delete.subtitle')}>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-xs text-foreground">
            <input
              type="checkbox"
              checked={deleteForm.confirm}
              onChange={(e) => setDeleteForm({ ...deleteForm, confirm: e.target.checked })}
              data-testid="delete-confirm-checkbox"
            />
            {t('delete.confirm_checkbox')}
          </label>
          {socialOnly ? (
            <div className="space-y-2" data-testid="delete-otp-flow">
              <p className="text-xs text-muted-foreground">{t('delete.social_hint')}</p>
              <div className="flex gap-2">
                <Input
                  inputMode="numeric"
                  maxLength={6}
                  placeholder={t('delete.otp_placeholder')}
                  value={deleteForm.otp}
                  onChange={(e) => setDeleteForm({ ...deleteForm, otp: e.target.value.replace(/\D/g, '') })}
                  className="h-9 bg-background"
                  disabled={!deleteOtpSent}
                  data-testid="delete-otp-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRequestDeleteOtp}
                  disabled={deleteOtpBusy}
                  className="h-9 shrink-0"
                  data-testid="delete-request-otp-btn"
                >
                  {deleteOtpBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : (deleteOtpSent ? t('delete.otp_resend') : t('delete.otp_send'))}
                </Button>
              </div>
            </div>
          ) : (
            <Input type="password" placeholder={t('delete.current_pwd_placeholder')}
              value={deleteForm.current_password}
              onChange={(e) => setDeleteForm({ ...deleteForm, current_password: e.target.value })}
              className="h-9 bg-background"
              data-testid="delete-pwd-input"
            />
          )}
          <Button
            onClick={handleDelete}
            disabled={deleteBusy || (socialOnly && !deleteOtpSent)}
            className="h-9 bg-red-600 text-white hover:bg-red-700"
            data-testid="delete-account-btn"
          >
            {deleteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><X className="mr-1 h-4 w-4" /> {t('delete.btn')}</>)}
          </Button>
        </div>
      </Card>

      {/* Phase 27.8 — Blocked users management */}
      <BlockedUsersSection />

      {/* Phase 43 — the previous Creator Fiscal Data Card was removed.
          Creators now activate their fiscal profile exclusively through
          the Creator Onboarding Wizard at /creator/onboarding, launched
          from the "Diventa VIP Creator" CTA in Profile. */}
    </div>
  );
};

export default SettingsPage;
